<template>
	<div class="xs-12">
		<!-- Headline -->
		<div class="flex xs-12 xs-between">
			<div class="fs5 lh125 fw7 c1 mb2">{{ $t("files") }}</div>
			<j-button
				class="tertiary"
				v-if="canWrite"
				@click="$modal.show('uploadFiles')"
			>
				<crm-icon name="add" size="small" color="c2" />
				{{ $t("add") }}
			</j-button>
		</div>

		<!-- Body -->
		<j-card class="bg4--l3">
			<j-list :list="computedList" v-if="!isLoading">
				<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
					<div class="flex wrap xs-10 xs-left xs-middle">
						<div
							class="xs-12 fs3 lh125 fw7 cu2 c2"
							@click="downloadFile(item.key, item.fileName)"
							:title="$t('created') + ': ' + item.created"
						>
							{{ item.fileName }}
						</div>
					</div>
					<div class="flex xs-2 xs-right xs-middle">
						<crm-icon
							class="cu2"
							name="delete"
							color="cError"
							@click.native="
								deleteFile({ fileName: item.fileName, key: item.key })
							"
						/>
					</div>
				</div>

				<!-- Empty -->
				<div class="xs-12 p4 ta2 fs3 c6" slot="empty">
					{{ $t("errors.no_data") }}
				</div>
			</j-list>

			<!-- Loading -->
			<div v-if="isLoading" class="p3 fs3 fw7 c6">{{ $t("loading") }}</div>
		</j-card>

		<!-- Modal for upload of files -->
		<core-modal name="uploadFiles" :headline="$t('upload_files')" width="800px">
			<core-files-upload
				:filesOf="computedFilesOf"
				:id="id"
				@upload:finished="$emit('files:uploaded')"
			/>
		</core-modal>
	</div>
</template>

<script>
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JList from "@/components/JList/"
import CrmIcon from "@/components/CrmIcon/"
import CoreModal from "@/modules/core/components/CoreModal"
import CoreFilesUpload from "@/modules/core/components/CoreFilesUpload"
import { mapState } from "vuex"
import dayjs from "dayjs"
import { can } from "@/permissions/"
import api from "../api/"
import axios from "axios"
import FileSaver from "file-saver"

/**
 * This component is responsible for displaying of files
 * It's a smart component, it can also delete files via integrated api call
 * It notifies it's parent that file was deleted via event "file:deleted"
 */

export default {
	components: {
		JButton,
		JCard,
		JList,
		CrmIcon,
		CoreModal,
		CoreFilesUpload
	},

	data() {
		return {
			isLoading: false
		}
	},

	props: {
		/**
		 * List of files to display
		 * Follows GraphQL type attachments
		 */
		list: {
			type: Array,
			required: false,
			default: () => [
				{
					fileName: null, // this serves as file id
					path: null,
					createdAt: null
				}
			]
		},
		/**
		 * We have bunch of APIs for files, determine what we should use
		 * This also determines permissions
		 */
		filesOf: {
			validator(val) {
				return (
					val === "individual" ||
					val === "company" ||
					val === "policy" ||
					val === "claim" ||
					val === "case" ||
					val === "templateAssets" ||
					val === "proposals" ||
					val === "custom"
				)
			},
			required: true
		},

		/**
		 * ID of entity or policy the files are attached to
		 */
		id: {
			type: String
		},

		canWrite: {
			type: Boolean,
			default: true
		}
	},

	computed: {
		computedList() {
			return this.list.map(item => ({
				...item,
				key: item.path || item.key,
				fileName: item.fileName,
				url: `https://api.asia.covergo.com/api/v1/files/${encodeURIComponent(
					item.path
				)}`,
				created: item.createdAt
					? dayjs(item.createdAt).format(this.$store.state.settings.dateFormat)
					: null
			}))
		},

		/**
		 * This computes prop for core-files-upload
		 * We have to pass "entity" or "policy"
		 */
		computedFilesOf() {
			if (this.filesOf === "policy") return "policy"
			if (this.filesOf === "claim") return "claim"
			if (this.filesOf === "case") return "case"
			if (this.filesOf === "templateAssets") return "templateAssets"
			if (this.filesOf === "proposals") return "proposals"
			if (this.filesOf === "custom") return "custom"
			else return "entity"
		}
	},

	methods: {
		/**
		 * We have API for downloading of files
		 */
		async downloadFile(key, fileName) {
			this.isLoading = true
			axios({
				url: `https://api.asia.covergo.com/api/v1/files/${encodeURIComponent(key)}`,
				headers: {
					Authorization: `Bearer ${window.TK}`
				},
				method: "GET",
				responseType: "blob" // important
			})
				.then(response => {
					this.isLoading = false
					FileSaver.saveAs(
						new Blob([response.data], { type: response.data.type }),
						fileName
					)
				})
				.catch(e => {
					this.isLoading = false
					this.$store.dispatch("showError", [this.$t("errors.general")])
				})
		},

		async deleteFile({ fileName, key }) {
			// NOTE: If delete is for claims, use new approach
			// Later on we have to refactor all files to use new approach for delete and also uploads
			if (
				this.filesOf === "claim" ||
				this.filesOf === "case" ||
				this.filesOf === "templateAssets" ||
				this.filesOf === "proposals"
			) {
				this.newDeleteFile({ fileName, key })
				return
			}

			this.isLoading = true
			// Define api key
			// We have 2 APIs fo deleting files
			// One is for policy and one for entity
			let apiKeys = { query: "deletePolicyAttachment", arg: "policyId" }
			if (this.filesOf !== "policy")
				apiKeys = { query: "deleteEntityAttachment", arg: "entityId" }

			const res = await api[apiKeys.query]({
				fileName,
				[apiKeys.arg]: this.id
			})

			this.isLoading = false

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				this.fileNameToDelete = null // return the item back into the list
				return
			}

			// Success
			this.$emit("file:deleted")
		},

		/**
		 * This is a new deleteFile that is using new API for delete
		 */
		async newDeleteFile({ key }) {
			this.isLoading = true
			const res = await api.deleteFile({ key })
			this.isLoading = false

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Success
			this.$emit("file:deleted")
		}
	}
}
</script>
